/**
 * Vertical geometry for the sidepanel: how tall it is and where in the
 * available band it sits.
 *
 * The panel is not a window — it is a CSS box inside the fullscreen overlay —
 * so this is the single place that decides its height/top. Everything that used
 * to subtract the dock independently (content max-height, footer margin, view
 * router bottom) now just fills the panel, because the band already excludes
 * the dock. Kept free of React and of `window` so the clamping rules are
 * unit-testable.
 */

/** Below this the section stack stops reading as a panel. */
export const SIDEPANEL_MIN_HEIGHT = 600

/**
 * Tallest the panel goes before the user drags it. A 4K display's band is
 * ~2000px, and a column that tall is mostly empty glass with its footer far
 * from its content — so large displays get a centered, human-scaled panel while
 * laptops (band < this) stay full-bleed.
 */
export const DEFAULT_SIDEPANEL_MAX_HEIGHT = 1200

export interface SidepanelVerticalLayout {
  /** Top of the available band, in overlay-viewport px. */
  bandTop: number
  /** Height of the available band (viewport minus the menu bar and a horizontally-overlapping bottom dock). */
  bandHeight: number
  /** Resolved panel height, clamped into the band. */
  height: number
  /** Resolved panel top, clamped so the panel stays inside the band. */
  top: number
}

export interface SidepanelVerticalLayoutInput {
  bandTop: number
  bandHeight: number
  /** Persisted drag height, or `null` while the user has never resized. */
  customHeight: number | null
  /** Persisted distance from the band's top, or `null` for centered. */
  customOffset: number | null
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/**
 * Clamps into the band. The minimum yields to the band on displays too short
 * to hold it, so the panel never overhangs rather than never shrinks.
 */
export function clampSidepanelHeight(height: number, bandHeight: number): number {
  const max = Math.max(0, bandHeight)
  return clamp(height, Math.min(SIDEPANEL_MIN_HEIGHT, max), max)
}

/** Clamps the distance from the band's top so the panel stays fully inside it. */
export function clampSidepanelVerticalOffset(offset: number, bandHeight: number, height: number): number {
  return clamp(offset, 0, Math.max(0, bandHeight - height))
}

/** Height the panel takes when the user has never resized it — and what a double-click resets to. */
export function defaultSidepanelHeight(bandHeight: number): number {
  return clampSidepanelHeight(Math.min(bandHeight, DEFAULT_SIDEPANEL_MAX_HEIGHT), bandHeight)
}

/** Offset that centers `height` in the band — the default when nothing is persisted. */
export function centeredSidepanelVerticalOffset(bandHeight: number, height: number): number {
  return Math.max(0, Math.round((bandHeight - height) / 2))
}

export function resolveSidepanelVerticalLayout({
  bandTop,
  bandHeight,
  customHeight,
  customOffset,
}: SidepanelVerticalLayoutInput): SidepanelVerticalLayout {
  const height = clampSidepanelHeight(customHeight ?? defaultSidepanelHeight(bandHeight), bandHeight)
  const offset = clampSidepanelVerticalOffset(
    customOffset ?? centeredSidepanelVerticalOffset(bandHeight, height),
    bandHeight,
    height,
  )

  return { bandTop, bandHeight, height, top: bandTop + offset }
}
